import { Fragment, MouseEvent, useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { useSidebarOpenState } from '@backstage/core-components';

import { ThemeSwitcher } from '@spotify/confidence-backstage-encore-theme';
import { useAuth } from '@spotify/confidence-core-react';
import {
  CurrentUserFragment,
  useCurrentUser,
} from '@spotify/plugin-confidence-iam-react';
import { SlackWorkspaceTooltip } from '@spotify/plugin-confidence-notifications-react';

import { FeatureFlagsToggles } from './FeatureFlagsToggles';
import { SidebarPinToggle } from './SidarbarPinToggle';
import { UserInfo } from './UserInfo';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: 'var(--bui-space-6)',
    height: 'var(--bui-space-6)',
    border: `2px solid ${theme.palette.divider}`,
  },
  button: {
    display: 'flex',
    color: theme.palette.navigation.color,
    padding: theme.spacing(2, 0),
    border: 'none',
    outline: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    background: 'transparent',
    cursor: 'pointer',
    width: '100%',
  },
  name: {
    color: theme.palette.navigation.selectedColor,
  },
  email: {
    color: theme.palette.navigation.color,
  },
}));

export function ProfileMenu() {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { currentUser } = useCurrentUser();
  const user = currentUser?.user;

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const { isOpen } = useSidebarOpenState();

  return (
    <Fragment>
      <button className={classes.button} onClick={openMenu}>
        <Avatar
          className={classes.avatar}
          src={user?.pictureUri ?? undefined}
        />
        {isOpen && (
          <Box
            marginLeft={1}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
          >
            <Typography noWrap variant="body1" className={classes.name}>
              {user?.fullName}
            </Typography>
            <Typography noWrap variant="body2" className={classes.email}>
              {user?.email}
            </Typography>
          </Box>
        )}
      </button>
      <Popover
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        // for some reason sometimes a scroll bar appears in the popover
        PaperProps={{
          style: {
            overflow: 'hidden',
          },
        }}
      >
        <UserInfo user={user} />
        <Slack user={user} />
        <ThemeSwitcher />
        <SidebarPinToggle />
        <FeatureFlagsToggles />
      </Popover>
    </Fragment>
  );
}

function Slack({ user }: { user?: CurrentUserFragment['user'] }) {
  const auth = useAuth();
  const connected = user?.slackUserInfo?.user;
  return (
    <ListItem ContainerComponent="div">
      <ListItemText
        primary="Slack"
        secondary={connected ? 'Connected' : 'Not connected'}
      />
      {!connected && (
        <ListItemSecondaryAction>
          <SlackWorkspaceTooltip>
            <Button
              size="small"
              variant="outlined"
              onClick={async () => {
                const t = await auth.getAccessTokenSilently();
                const url = `https://slack.com/openid/connect/authorize?scope=openid&response_type=code&redirect_uri=https://app.confidence.spotify.com/slack&client_id=5591942043344.7641356973315&nonce=${t}`;
                window.location.href = url;
              }}
            >
              Connect
            </Button>
          </SlackWorkspaceTooltip>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
}
